import moment from 'moment';
import { CUSTOMER_SERVICE_NUMBER } from '../constants/common';
import {
  NICKNAME_MAX_LENGTH,
  NICKNAME_MIN_LENGTH,
  ZIPCODE_LENGTH,
} from '../constants/myWalletPage';
import { getStateCodeFromParsedUrl } from './navigationHelper';

// getOnlyDigitsInput returns only digits
export const getOnlyDigitsInput = (value: string): string => {
  return value.replace(/\D/g, '');
};

// getInputWithoutSpace returns output without space
export const getInputWithoutSpace = (value: string): string => {
  return value.replace(/\s/g, '');
};

// isSlashPresent checks if slash is present or not
export const isSlashPresent = (value: string): RegExpMatchArray | null => {
  return value.match(/\//);
};

// getAmountInput allows only hyphen, numbers, comma and dot
export const getAmountInput = (value: string): string => {
  return value.replace(/[^0-9.-]/g, '').replace(/^(-)|-+/g, '$1');
};

// addCommas add commas after 3 digits
export const addCommas = (value: string): string => {
  return getAmountInput(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// addCommasBeforeDecimal add commas before decimal point
export const withCommas = (value: string): string => {
  const amountValue = value.split('.');
  const amountWithCommas = `${addCommas(amountValue[0])}${
    typeof amountValue[1] === 'string' ? `.${amountValue[1]}` : ''
  }`;
  return amountWithCommas;
};

// removeCommaFromAmount removes comma
export const removeCommaFromAmount = (value: string): string => {
  return value.replace(/[,]+/g, '');
};

// formatAmount 2 decimal values after decimal point
export const formatAmount = (value: string): string => {
  if (value) {
    return Number(removeCommaFromAmount(value)).toFixed(2).toString();
  } else {
    return '';
  }
};

// getAmount Converts string into number
export const getAmount = (value: string): number => {
  return Number(removeCommaFromAmount(value));
};

export const removeAllSpaces = (text: string): string => {
  return text.replace(/ /g, '');
};

export const getFormatedDateInMonthDay = (
  value: string | undefined,
): string => {
  const dueDate = moment(value, 'YYYY-MM-DD');
  return dueDate.format('MMM') + ' ' + dueDate.format('D');
};

//Validate Email
export const isEmailIdValid = (emaildId: string): boolean => {
  const emailIdRegex = /^[a-zA-Z]+[a-zA-Z0-9]*@[a-zA-Z]+\.[a-zA-Z]+$/g;
  return emailIdRegex.test(emaildId);
};

//Validate Email for fiserv binder pay later make payment
export const isEmailValidFiserv = (emailId: string): boolean => {
  // eslint exception added to bypass escape character in regex
  // eslint-disable-next-line
  const regexExp = new RegExp(
    "[a-zA-Z0-9!#$%&amp;'*+/=?^_`{|}~-]+(?:.[a-zA-Z0-9!#$%&amp;'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?",
  );
  return regexExp.test(emailId || '');
};

// returns State Specific Customer Service Number
export const getStateSpecificCustomerServiceNumber = () => {
  const stateCode = getStateCodeFromParsedUrl();
  const stateServiceNumber = CUSTOMER_SERVICE_NUMBER[stateCode];
  return stateServiceNumber;
};

// convert milliseconds to minutes, see session timeout dialog
export const getTimeInMinutes = (time: number): string => {
  return `[${Math.floor(time / 60000)}:${Number((time % 60000) / 1000)
    .toString()
    .padStart(2, '0')}]`;
};

// return payment accept terms text for checkbox
export const getCheckboxPaymentText = (value: string) => {
  const checkboxText = value ? value.split('$') : '';
  const checkboxPaymentText = {
    iUnderstandText: checkboxText[0],
    termsOfUseText: checkboxText[1],
  };
  return checkboxPaymentText;
};

export const isPaymentWalletNickNameValid = (nickName: string) => {
  const regex = new RegExp('^((?!\\d{14,})[a-zA-Z0-9 .,_-])*$', 'g');
  const validateLength =
    nickName.length >= NICKNAME_MIN_LENGTH &&
    nickName.length <= NICKNAME_MAX_LENGTH;

  return regex.test(nickName) && validateLength;
};

export const checkIfSpecialCharactersArePersent = (value: string) => {
  const specialCharacters = /[^A-Za-z 0-9]/g;
  return specialCharacters.test(value);
};

export const isPaymentWalletZipCodeValid = (zipCode: string) => {
  return (
    zipCode.length === ZIPCODE_LENGTH && new RegExp('^\\d+$', 'g').test(zipCode)
  );
};

export const getCurrencyFormat = (value: number | undefined): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(value));
};

export const appendHttpsToRedirectUri = (redirectURI?: string) => {
  if (!redirectURI?.startsWith('http')) {
    return `https://${redirectURI}`;
  }
  return redirectURI;
};

export const negateValue = (value: number, negativeOnly = false) => {
  if (value >= 0) {
    if (negativeOnly) {
      return getCurrencyFormat(value * -1);
    } else {
      return `+${getCurrencyFormat(value)}`;
    }
  } else {
    return getCurrencyFormat(value);
  }
};

export const appendParam = ({
  link,
  paramKey,
  paramValue,
}: {
  link: string;
  paramKey: string;
  paramValue: string | number;
}) => {
  if (link.includes('?')) {
    return `${link}&${paramKey}=${paramValue}`;
  } else {
    return `${link}?${paramKey}=${paramValue}`;
  }
};
